import { useState } from 'react';
import { createSession, ApiError } from '../lib/api';
import { ANATOMY_EMOJI } from '../lib/anatomyEmoji';
import type { AgeRange, Anatomy } from '../types';

interface OnboardingFormProps {
  layout?: 'stacked' | 'split';
  onSessionCreated: (result: {
    sessionId: string;
    partnerAToken: string;
    partnerBToken: string;
    partnerBShareUrl: string;
  }) => void;
}

interface FormData {
  partnerAName: string;
  partnerAAge: AgeRange | '';
  partnerAAnatomy: Anatomy | '';
  partnerBName: string;
  partnerBAge: AgeRange | '';
  partnerBAnatomy: Anatomy | '';
}

const AGE_OPTIONS: AgeRange[] = ['18-24', '25-29', '30-34', '35-39', '40-44', '45-49', '50-54', '55+'];
const ANATOMY_OPTIONS: { value: Anatomy; emoji: string; label: string }[] = [
  { value: 'vulva', emoji: ANATOMY_EMOJI.vulva, label: 'Vagina' },
  { value: 'penis', emoji: ANATOMY_EMOJI.penis, label: 'Penis' },
];

export default function OnboardingForm({
  layout = 'stacked',
  onSessionCreated,
}: OnboardingFormProps) {
  const [form, setForm] = useState<FormData>({
    partnerAName: '',
    partnerAAge: '',
    partnerAAnatomy: '',
    partnerBName: '',
    partnerBAge: '',
    partnerBAnatomy: '',
  });
  const [errors, setErrors] = useState<Partial<Record<keyof FormData, string>>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);

  function updateField<K extends keyof FormData>(field: K, value: FormData[K]) {
    setForm((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: undefined }));
    setApiError(null);
  }

  function validate(): boolean {
    const next: Partial<Record<keyof FormData, string>> = {};

    if (!form.partnerAName.trim()) next.partnerAName = '(Nick)name is required';
    else if (form.partnerAName.length > 50) next.partnerAName = 'Max 50 characters';

    if (!form.partnerAAge) next.partnerAAge = 'Select an age range';
    if (!form.partnerAAnatomy) next.partnerAAnatomy = 'Select anatomy';

    if (!form.partnerBName.trim()) next.partnerBName = '(Nick)name is required';
    else if (form.partnerBName.length > 50) next.partnerBName = 'Max 50 characters';

    if (!form.partnerBAge) next.partnerBAge = 'Select an age range';
    if (!form.partnerBAnatomy) next.partnerBAnatomy = 'Select anatomy';

    setErrors(next);
    return Object.keys(next).length === 0;
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!validate()) return;

    setIsSubmitting(true);
    setApiError(null);

    try {
      const result = await createSession({
        partnerAName: form.partnerAName.trim(),
        partnerAAge: form.partnerAAge as AgeRange,
        partnerAAnatomy: form.partnerAAnatomy as Anatomy,
        partnerBName: form.partnerBName.trim(),
        partnerBAge: form.partnerBAge as AgeRange,
        partnerBAnatomy: form.partnerBAnatomy as Anatomy,
      });

      onSessionCreated(result);
    } catch (err) {
      if (err instanceof ApiError) {
        setApiError(err.message);
      } else {
        setApiError('Something went wrong. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-5 relative z-10">
      <div className={layout === 'split' ? 'space-y-5 md:grid md:grid-cols-[1fr_auto_1fr] md:gap-6 md:items-start md:space-y-0' : 'space-y-5'}>
        {layout === 'split' ? (
          <>
            <PartnerSection
              title="You"
              anatomyPrompt=""
              nameValue={form.partnerAName}
              onNameChange={(v) => updateField('partnerAName', v)}
              nameError={errors.partnerAName}
              ageValue={form.partnerAAge}
              onAgeChange={(v) => updateField('partnerAAge', v as AgeRange)}
              ageError={errors.partnerAAge}
              anatomyValue={form.partnerAAnatomy}
              onAnatomyChange={(v) => updateField('partnerAAnatomy', v as Anatomy)}
              anatomyError={errors.partnerAAnatomy}
              namePlaceholder="Your (nick)name"
            />

            {/* Divider — horizontal on mobile, vertical on md+ */}
            <div className="flex items-center justify-center py-1 md:flex-col md:py-0 md:self-stretch">
              <div className="h-px bg-white/10 flex-1 md:h-auto md:w-px md:flex-1" />
              <span className="px-3 md:px-0 md:py-3 text-[10px] text-slate-600 font-bold uppercase tracking-widest">+</span>
              <div className="h-px bg-white/10 flex-1 md:h-auto md:w-px md:flex-1" />
            </div>

            <PartnerSection
              title="Your Partner"
              anatomyPrompt=""
              nameValue={form.partnerBName}
              onNameChange={(v) => updateField('partnerBName', v)}
              nameError={errors.partnerBName}
              ageValue={form.partnerBAge}
              onAgeChange={(v) => updateField('partnerBAge', v as AgeRange)}
              ageError={errors.partnerBAge}
              anatomyValue={form.partnerBAnatomy}
              onAnatomyChange={(v) => updateField('partnerBAnatomy', v as Anatomy)}
              anatomyError={errors.partnerBAnatomy}
              namePlaceholder="Their (nick)name"
            />
          </>
        ) : (
          <>
            <PartnerSection
              title="You"
              anatomyPrompt=""
              nameValue={form.partnerAName}
              onNameChange={(v) => updateField('partnerAName', v)}
              nameError={errors.partnerAName}
              ageValue={form.partnerAAge}
              onAgeChange={(v) => updateField('partnerAAge', v as AgeRange)}
              ageError={errors.partnerAAge}
              anatomyValue={form.partnerAAnatomy}
              onAnatomyChange={(v) => updateField('partnerAAnatomy', v as Anatomy)}
              anatomyError={errors.partnerAAnatomy}
              namePlaceholder="Your (nick)name"
            />

            <div className="flex items-center justify-center py-1">
              <div className="h-px bg-white/10 flex-1" />
              <span className="px-3 text-[10px] text-slate-600 font-bold uppercase tracking-widest">+</span>
              <div className="h-px bg-white/10 flex-1" />
            </div>

            <PartnerSection
              title="Your Partner"
              anatomyPrompt=""
              nameValue={form.partnerBName}
              onNameChange={(v) => updateField('partnerBName', v)}
              nameError={errors.partnerBName}
              ageValue={form.partnerBAge}
              onAgeChange={(v) => updateField('partnerBAge', v as AgeRange)}
              ageError={errors.partnerBAge}
              anatomyValue={form.partnerBAnatomy}
              onAnatomyChange={(v) => updateField('partnerBAnatomy', v as Anatomy)}
              anatomyError={errors.partnerBAnatomy}
              namePlaceholder="Their (nick)name"
            />
          </>
        )}
      </div>

      {/* API error */}
      {apiError && (
        <div className="p-3 rounded-xl bg-red-500/10 border border-red-400/40">
          <p className="text-sm text-red-300">{apiError}</p>
        </div>
      )}

      {/* Submit */}
      <button
        type="submit"
        disabled={isSubmitting}
        className="w-full gradient-cta onboarding-cta-glow text-white font-extrabold py-4 rounded-2xl text-lg flex items-center justify-center gap-2 disabled:opacity-50 active:scale-[0.99] transition-transform"
      >
        {isSubmitting ? 'Creating your quiz...' : 'Start the Quiz 🔥'}
      </button>
      <p className="text-xs text-slate-500 font-medium text-center">
        100% anonymous &middot; Results in ~10 mins
      </p>
    </form>
  );
}

// ── Partner form section ─────────────────────────────────────────────

function PartnerSection({
  title,
  anatomyPrompt,
  nameValue,
  onNameChange,
  nameError,
  ageValue,
  onAgeChange,
  ageError,
  anatomyValue,
  onAnatomyChange,
  anatomyError,
  namePlaceholder,
  showAge = true,
  showAnatomy = true,
}: {
  title: string;
  anatomyPrompt: string;
  nameValue: string;
  onNameChange: (v: string) => void;
  nameError?: string;
  ageValue: string;
  onAgeChange: (v: string) => void;
  ageError?: string;
  anatomyValue: string;
  onAnatomyChange: (v: string) => void;
  anatomyError?: string;
  namePlaceholder: string;
  showAge?: boolean;
  showAnatomy?: boolean;
}) {
  return (
    <div className="text-left space-y-3">
      <label className="text-xs font-bold uppercase tracking-wider text-slate-400 ml-1">
        {title}
      </label>

      {/* Name input */}
      <input
        type="text"
        value={nameValue}
        onChange={(e) => onNameChange(e.target.value)}
        placeholder={namePlaceholder}
        maxLength={50}
        className={`w-full px-4 py-3.5 rounded-2xl border text-base outline-none onboarding-input transition-all ${
          nameError ? 'border-red-400' : 'border-white/10'
        }`}
        aria-label={`${title} nickname`}
      />
      {nameError && <p className="text-xs text-red-500">{nameError}</p>}

      {/* Age range */}
      {showAge && (
        <div className="relative">
          <select
            value={ageValue}
            onChange={(e) => onAgeChange(e.target.value)}
            className={`w-full px-4 py-3.5 rounded-2xl border text-base outline-none appearance-none pr-10 transition-all onboarding-input ${
              ageError ? 'border-red-400' : 'border-white/10'
            }`}
            aria-label={`${title} age range`}
          >
            <option value="">Select age range</option>
            {AGE_OPTIONS.map((age) => (
              <option key={age} value={age}>
                {age}
              </option>
            ))}
          </select>
          <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-slate-500">
            <svg className="w-4 h-4" viewBox="0 0 20 20" fill="none" aria-hidden="true">
              <path
                d="M5 7.5L10 12.5L15 7.5"
                stroke="currentColor"
                strokeWidth="1.75"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </span>
          {ageError && <p className="mt-1 text-xs text-red-500">{ageError}</p>}
        </div>
      )}

      {/* Anatomy */}
      {showAnatomy && (
        <div className="space-y-2">
          {anatomyPrompt && (
            <span className="block text-sm text-slate-400 font-bold">
              {anatomyPrompt}
            </span>
          )}
          <div className="grid grid-cols-2 gap-3">
            {ANATOMY_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                type="button"
                onClick={() => onAnatomyChange(opt.value)}
                className={`flex items-center justify-center gap-2 py-3.5 rounded-2xl border-2 text-sm font-bold transition-all ${
                  anatomyValue === opt.value
                    ? 'onboarding-anatomy-selected'
                    : 'onboarding-anatomy-default hover:bg-white/10 hover:text-slate-200'
                }`}
              >
                <span className="text-lg">{opt.emoji}</span> {opt.label}
              </button>
            ))}
          </div>
          {anatomyError && <p className="mt-1 text-xs text-red-500">{anatomyError}</p>}
        </div>
      )}
    </div>
  );
}
